import React, { useState } from 'react';
import { 
  X, User, FileText, Activity, MessageSquare, Mail, Phone, 
  MapPin, Briefcase, GraduationCap, Award, Calendar, DollarSign 
} from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import NotesTab from '../shared/NotesTab';
import ActivityTab from '../shared/ActivityTab';
import { formatSalary, formatDateAdded } from '../../utils/formatters';

const CandidateDetailModal = ({ candidate, isOpen, onClose, submissions = [], onEdit, selectedUser }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Get current user
  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  // Check if current user can edit this candidate
  // STRICT: Only the PRIMARY recruiter can edit (no team inheritance or name fallbacks for edit permission)
  const canEdit = React.useMemo(() => {
    if (!candidate) return false;
    const user = selectedUser || currentUser;
    if (!user) return false;
    
    // ONLY check if user is the primary recruiter (by UUID)
    // This ensures edit permission is restricted to the assigned recruiter only
    return candidate.primary_recruiter_id === user.id;
  }, [candidate, selectedUser, currentUser]);

  if (!candidate) return null;

  // Handle edit button click
  const handleEditClick = () => {
    if (onEdit) {
      onEdit(candidate);
    }
  };

  // Get candidate's submissions
  const candidateSubmissions = submissions.filter(s => s.candidate_id === candidate.id);

  // Format candidate name
  const candidateName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();

  // Format phone numbers
  const primaryPhone = candidate.primary_phone 
    ? `${candidate.primary_phone.countryCode} ${candidate.primary_phone.number}`
    : candidate.phone || candidate.mobile_phone || 'N/A';

  const secondaryPhone = candidate.secondary_phone
    ? `${candidate.secondary_phone.countryCode} ${candidate.secondary_phone.number}`
    : candidate.work_phone || null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="candidate-detail-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {candidate.display_id && (
                <span className="inline-block px-3 py-1.5 text-base font-mono font-bold bg-purple-100 text-purple-800 rounded-lg border-2 border-purple-300">
                  {candidate.display_id}
                </span>
              )}
              <h2 
                className="text-2xl font-semibold text-foreground"
                style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}
                data-testid="candidate-name-header"
              >
                {candidateName}
              </h2>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {candidate.current_title && (
                <div className="flex items-center gap-1">
                  <Briefcase className="h-4 w-4" />
                  <span>{candidate.current_title}</span>
                </div>
              )}
              {candidate.current_company && (
                <>
                  <span>at</span>
                  <span className="font-medium">{candidate.current_company}</span>
                </>
              )}
            </div>
            {candidate.location && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <MapPin className="h-4 w-4" />
                <span>{candidate.location}</span>
              </div>
            )}
          </div>
          
          {/* Status Badge */}
          <Badge 
            variant={candidate.candidate_status === 'Active' ? 'default' : 'secondary'}
            className="ml-4"
            data-testid="candidate-status-badge"
          >
            {candidate.candidate_status || 'Active'}
          </Badge>
        </div>

        <Separator className="mb-4" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mb-4" data-testid="candidate-detail-tabs">
            <TabsTrigger value="profile" className="gap-2" data-testid="tab-profile">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
              <Activity className="h-4 w-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
              <MessageSquare className="h-4 w-4" />
              Notes
            </TabsTrigger>
            <TabsTrigger value="resume" className="gap-2" data-testid="tab-resume">
              <FileText className="h-4 w-4" />
              Resume
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6" data-testid="profile-content">
              {/* Candidate ID - Prominent Display */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-purple-700">Candidate ID:</span>
                  {candidate.display_id ? (
                    <span className="inline-block px-3 py-1.5 text-base font-mono font-bold bg-purple-100 text-purple-800 rounded border-2 border-purple-300">
                      {candidate.display_id}
                    </span>
                  ) : (
                    <span className="inline-block px-3 py-1.5 text-base font-mono font-bold bg-red-100 text-red-800 rounded border-2 border-red-300">
                      ERROR: Missing ID
                    </span>
                  )}
                </div>
              </div>
              
              {/* Contact Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm" data-testid="candidate-email">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">Email:</span>
                    <a href={`mailto:${candidate.email}`} className="text-primary hover:underline">
                      {candidate.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-testid="candidate-phone">
                    <Phone className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">Phone:</span>
                    <span>{primaryPhone}</span>
                  </div>
                  {secondaryPhone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary" />
                      <span className="text-muted-foreground">Secondary:</span>
                      <span>{secondaryPhone}</span>
                    </div>
                  )}
                  {candidate.alternate_email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-primary" />
                      <span className="text-muted-foreground">Alt Email:</span>
                      <span>{candidate.alternate_email}</span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Professional Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {candidate.total_experience_years !== undefined && (
                    <div className="flex items-start gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Experience:</span>
                        <span className="ml-1 font-medium">{candidate.total_experience_years} years</span>
                      </div>
                    </div>
                  )}
                  {candidate.current_salary && (
                    <div className="flex items-start gap-2 text-sm">
                      <DollarSign className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Current Salary:</span>
                        <span className="ml-1 font-medium">{formatSalary(candidate.current_salary)}</span>
                      </div>
                    </div>
                  )}
                  {candidate.salary_expectations && (
                    <div className="flex items-start gap-2 text-sm">
                      <DollarSign className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Expected:</span>
                        <span className="ml-1 font-medium">
                          {formatSalary(candidate.salary_expectations.min)} - {formatSalary(candidate.salary_expectations.max)}
                        </span>
                      </div>
                    </div>
                  )}
                  {candidate.availability_date && (
                    <div className="flex items-start gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Available:</span>
                        <span className="ml-1 font-medium">{new Date(candidate.availability_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                  {candidate.work_authorization_status && (
                    <div className="flex items-start gap-2 text-sm">
                      <Award className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Work Auth:</span>
                        <span className="ml-1 font-medium capitalize">{candidate.work_authorization_status.replace('_', ' ')}</span>
                      </div>
                    </div>
                  )}
                  {candidate.remote_work_preference && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Work Pref:</span>
                        <span className="ml-1 font-medium capitalize">{candidate.remote_work_preference.replace('_', ' ')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Skills */}
              {candidate.skills && candidate.skills.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                      Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {candidate.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Summary */}
              {candidate.professional_summary && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                      Professional Summary
                    </h3>
                    <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                      {candidate.professional_summary}
                    </p>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Resume Tab */}
            <TabsContent value="resume" className="space-y-6" data-testid="resume-content">
              {/* Work Experience */}
              {candidate.work_experience && candidate.work_experience.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                    Work Experience
                  </h3>
                  <div className="space-y-4">
                    {candidate.work_experience.map((exp, index) => (
                      <div key={index} className="border-l-2 border-primary/30 pl-4 py-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-foreground">{exp.title}</h4>
                            <p className="text-sm text-muted-foreground">{exp.company}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {exp.is_current ? 'Current' : `${exp.start_date} - ${exp.end_date}`}
                          </Badge>
                        </div>
                        {exp.description && (
                          <p className="text-sm text-foreground/70 mt-2">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {candidate.education && candidate.education.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                      Education
                    </h3>
                    <div className="space-y-3">
                      {candidate.education.map((edu, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <GraduationCap className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-foreground">{edu.degree} in {edu.field_of_study}</h4>
                            <p className="text-sm text-muted-foreground">{edu.institution}</p>
                            {edu.graduation_year && (
                              <p className="text-xs text-muted-foreground">Graduated: {edu.graduation_year}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Certifications */}
              {candidate.certifications && candidate.certifications.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                      Certifications
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {candidate.certifications.map((cert, index) => (
                        <Badge key={index} variant="secondary" className="text-sm">
                          <Award className="h-3 w-3 mr-1" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Resume File */}
              {candidate.resume_file_url && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                      Resume Document
                    </h3>
                    <Button 
                      variant="outline" 
                      className="gap-2"
                      onClick={() => window.open(candidate.resume_file_url, '_blank')}
                      data-testid="download-resume-btn"
                    >
                      <FileText className="h-4 w-4" />
                      View/Download Resume
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Activity Tab */}
            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4" data-testid="activity-content">
              <ActivityTab 
                entityType="candidate" 
                entityId={candidate?.id} 
              />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4" data-testid="notes-content">
              <NotesTab 
                entityType="candidate" 
                entityId={candidate?.id}
                currentUser={currentUser}
              />
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border">
          <Button 
            variant="outline" 
            onClick={onClose}
            data-testid="close-modal-btn"
          >
            Close
          </Button>
          {/* Edit button only shows for "My Candidates" */}
          {canEdit && onEdit && (
            <Button 
              variant="default"
              onClick={handleEditClick}
              data-testid="edit-candidate-btn"
            >
              Edit Candidate
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateDetailModal;
